import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

// Define the types for NOC Outjob Request
export interface NOCOutjobRequest {
  id: number;
  is_draft?: boolean;
  status?: string;
  referenceNumber: string;
  empID?: string;
  approverId?: string;
  employeeName?: string; // Added employee name field
  postAppliedFor?: string;
  organisation?: string;
  organisationInWhich?: string;
  organizationApplied?: string;
  natureOfOutside?: string;
  natureOfOutsideJob?: string;
  natureOfOutsideTitle?: string; // Added field
  natureOfOutsideJobTitle?: string | null; // Added field
  advertisementNo?: string;
  advertisementDate?: string;
  lastDateOfApplica?: string;
  lastDateReceipt?: string;
  requestStatus?: string;
  requestCreationDat?: string;
  created_at: string;
  modified_at: string;
  // Additional fields for comprehensive details
  appNoInYear?: string;
  placeofPosting?: string;
  employeeStatus?: string;
  department?: string;
  contactNumber?: string;
  whetherProbation?: string;
  whetherProbationConfirmed?: string;
  probationConfirmed?: string;
  VigilancePending?: string;
  trainedAbroadByDM?: string;
  placeAbroadTrain?: string;
  yearOfAbroadTrain?: string;
  newspaperWebsite?: string;
  // Newly added fields for outside job details
  outsideAddress?: string;
  addressDetails?: string;
  natureOfAssociation?: string;
  expectedDateOfJoining?: string;
  expectedDurationOfAssociation?: string;
  duties?: string;
  remuneration?: string;
  age?: string;
  caste?: string;
  dandARPendingOrNot?: string;
  vigilancePending?: string;
  dandARStatus?: string;
  dandARPenaltyEffective?: string;
  finalDandARVigilance?: string;
  approverName?: string;
  approverUserID?: string;
  approverLevel?: string;
  timeStamp?: string;
  remarks?: string;
  remarks1?: string;
  // Related data from joins
  natureDetails?: {
    id: number | null;
    natureId: string;
    natureTitle: string;
  };
  documents?: Array<{
    id: number;
    fileName: string;
    typeOfAttachedDoc: string;
    fileExtension?: string;
    fileSize?: string;
    content?: string;
  }>;
  advertisingDocuments?: Array<{
    id: number;
    fileName: string;
    typeOfAttachedDoc: string;
    fileExtension?: string;
    fileSize?: string;
    content?: string;
  }>;
  education?: Array<{
    edu_documents: Array<{
      id: number;
      referenceNumber: string;
      fileName: string;
    }>;
    id: number;
    courseName: string;
    courseDateOfPassing: string;
    courseDateOfAdmission: string;
    qualificationAcquir: string;
    courceAfflitaed: string;
    educationDocuments?: Array<{
      id: number;
      fileName: string;
      typeOfAttachedDoc: string;
      fileExtension?: string;
      fileSize?: string;
      content?: string;
    }>;
  }>;
  // Employee details from join
  employeeDetails?: {
    empId: string;
    fullName?: string;
    firstname?: string;
    secondname?: string;
    image?: string;
    email?: string;
    phone?: string;
    [key: string]: any;
  };
  employee?: any;
  emailId?: string;
  dateOfBirth?: string;
  departmentName?: string;
  contact_info?: string;
  category?: string;
  [key: string]: any;
}

export interface NOCOutjobState {
  requests: NOCOutjobRequest[];
  currentRequest: NOCOutjobRequest | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  uploadLoading: boolean;
  deleteLoading: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Define the API response type
interface NOCOutjobResponse {
  data: NOCOutjobRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Define the request parameters
interface FindAllNOCOutjobParams {
  page?: number;
  limit?: number;
  orderColumn?: string;
  orderBy?: string;
  query?: string;
  requestStatus?: string;
  startDate?: string;
  endDate?: string;
}

// Define the create NOC request parameters
interface CreateNOCOutjobParams {
  whetherProbation?: string;
  is_draft?: boolean;
  whetherProbationConfirmed?: string;
  probationConfirmed?: string;
  trainedAbroadByDM?: string;
  appNoInYear?: string;
  placeofPosting?: string;
  natureOfOutside?: string;
  natureOfOutsideJob?: string;
  postAppliedFor?: string;
  organizationApplied?: string;
  newspaperWebsite?: string;
  advertisementNo?: string;
  advertisementDate?: string;
  organisationInWhich?: string;
  lastDateOfApplica?: string;
  lastDateReceipt?: string;
  dandARPenaltyEffective?: string;
  vigilancePenaltyEffective?: string;
  education?: Array<{
    courseName?: string;
    courseDateOfPassing?: string;
    qualificationAcquir?: string;
    isCourseRecog?: string;
    courseDateOfAdmission?: string;
    courceAfflitaed?: string;
    qualification?: string;
    documents?: Array<{
      fileName: string;
      typeOfAttachedDoc?: string;
    }>;
  }>;
  documents?: Array<{ fileName: string }>;
}

// File upload parameters
interface FileUploadParams {
  filestring: string;
}

// File delete parameters
interface FileDeleteParams {
  key: string;
}

// NOC request delete parameters
interface DeleteNOCOutjobParams {
  id: number;
}

// Initial state
const initialState: NOCOutjobState = {
  requests: [],
  currentRequest: null,
  loading: false,
  error: null,
  createLoading: false,
  uploadLoading: false,
  deleteLoading: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

// Async thunk for fetching NOC outjob requests
export const fetchNOCOutjobRequests = createAsyncThunk(
  "nocOutjob/fetchRequests",
  async (params: FindAllNOCOutjobParams = {}, { rejectWithValue }) => {


    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ALL_OUTJOB_REQUESTS,
        params
      );

      // Handle the nested response structure
      const responseData = response.data;

      if (responseData && responseData.data) {
        const innerData = responseData.data;
        if (innerData && typeof innerData === "object" && "data" in innerData) {
          const extractedData = {
            data: innerData.data || [],
            total: innerData.total || 0,
            page: innerData.page || 1,
            limit: innerData.limit || 10,
            totalPages: innerData.totalPages || 0,
          };
          return extractedData as NOCOutjobResponse;
        } else if (Array.isArray(innerData)) {
          // Case: responseData.data is directly the array
          const extractedData = {
            data: innerData,
            total: innerData.length,
            page: 1,
            limit: innerData.length,
            totalPages: 1,
          };
          return extractedData as NOCOutjobResponse;
        } else {
          console.error("Unexpected data structure:", innerData);
          console.error("Type of innerData:", typeof innerData);
          throw new Error("Unexpected API response data structure");
        }
      } else {
        console.error("Invalid API response structure:", responseData);
        throw new Error("Invalid API response structure");
      }
    } catch (error: any) {
      console.error("API call failed:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchNOCOutjobById = createAsyncThunk(
  "nocOutjob/fetchById",
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.NOC.FIND_ALL_OUTJOB_REQUESTS, {
        page: 1,
        limit: 10,
      });
      const responseData = response.data;
      let items: any[] = [];
      if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
        items = responseData.data.data;
      } else if (Array.isArray(responseData?.data)) {
        items = responseData.data;
      } else if (responseData?.data) {
        items = [responseData.data];
      }

      const item =
        items.find(
          (r: any) =>
            r.id === Number(id) ||
            r.id === String(id) ||
            r.referenceNumber === String(id)
        ) || items[0] || null;

      return item;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);


// Async thunk for creating NOC outjob request
export const createNOCOutjobRequest = createAsyncThunk(
  "nocOutjob/createRequest",
  async (params: CreateNOCOutjobParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.CREATE_OUTJOB_REQUEST,
        params
      );
      return response.data;
    } catch (error: any) {
      console.log("Error creating NOC outjob request:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for uploading file to AWS
export const uploadFileToAWS = createAsyncThunk(
  "nocOutjob/uploadFile",
  async (params: FileUploadParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for deleting file from AWS
export const deleteFileFromAWS = createAsyncThunk(
  "nocOutjob/deleteFile",
  async (params: FileDeleteParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.DELETE_FILE, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for deleting NOC outjob request
export const deleteNOCOutjobRequest = createAsyncThunk(
  "nocOutjob/deleteRequest",
  async (params: DeleteNOCOutjobParams, { rejectWithValue }) => {
    try {
      const requestBody = {
        id: params.id,
      };
      const response = await Api.post(
        API_URLS.NOC.DELETE_OUTJOB_REQUEST,
        requestBody
      );
      return { ...response.data, deletedId: params.id };
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create the slice
const nocOutjobSlice = createSlice({
  name: "nocOutjob",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
    setCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestId = action.payload;
      const request = state.requests.find((req) => req.id === requestId);
      state.currentRequest = request || null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch NOC outjob requests
      .addCase(fetchNOCOutjobRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCOutjobRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchNOCOutjobRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNOCOutjobById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCOutjobById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;
      })
      .addCase(fetchNOCOutjobById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create NOC outjob request
      .addCase(createNOCOutjobRequest.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createNOCOutjobRequest.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createNOCOutjobRequest.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Upload file to AWS
      .addCase(uploadFileToAWS.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadFileToAWS.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadFileToAWS.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
      })
      // Delete file from AWS
      .addCase(deleteFileFromAWS.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteFileFromAWS.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteFileFromAWS.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Delete NOC outjob request
      .addCase(deleteNOCOutjobRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNOCOutjobRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove the deleted request from the state
        state.requests = state.requests.filter(
          (request) => request.id !== action.payload.deletedId
        );
        // Update pagination total
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })
      .addCase(deleteNOCOutjobRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentRequest,
  setCurrentRequest,
  setPage,
  setLimit,
} = nocOutjobSlice.actions;
export default nocOutjobSlice.reducer;
